import { ClassRoomPlanFragmentFragment } from "../../gql/graphql.ts";
import { formatDate, formatDateTime } from "../../UI/Utils/formatDate.ts";
import dayjs, { Dayjs } from "dayjs";
import { NormaPopover } from "../../UI/NormaPopover.tsx";
import { Card } from "../../UI/Card.tsx";
import {
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline/index.js";
import { PrimaryLoadingIndicator } from "../../UI/Loading/PrimaryLoadingIndicator.tsx";
import { LoaderSize } from "../../UI/Loading/LoaderSize.tsx";
import { TextButton } from "../../UI/TextButton.tsx";

type BonusItem = NonNullable<ClassRoomPlanFragmentFragment["bonuses"]>[number];

function BonusBadge({ bonus }: { bonus: BonusItem }) {
  if (bonus.type === "all_you_can_speak") {
    const fmt = (d: string) => formatDate(dayjs(d).utc());
    const period =
      bonus.activeFrom && bonus.activeTo
        ? ` · ${fmt(bonus.activeFrom)} → ${fmt(bonus.activeTo)}`
        : bonus.activeFrom
          ? ` · from ${fmt(bonus.activeFrom)}`
          : bonus.activeTo
            ? ` · until ${fmt(bonus.activeTo)}`
            : "";
    const countLabel =
      bonus.lessonsCountedPerWeek === 0
        ? "∞"
        : `${bonus.lessonsCountedPerWeek}L/w`;
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
        AYCS {countLabel}
        {period}
      </span>
    );
  }
  if (bonus.type === "extra_lessons") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        +{bonus.extraLessons} lezioni
      </span>
    );
  }
  if (bonus.type === "extra_weeks") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
        +{bonus.extraWeeks} settimane
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
      {bonus.type}
    </span>
  );
}

type Props = {
  plan: ClassRoomPlanFragmentFragment;
  onDelete: (planId: string) => void;
  onEdit: (plan: ClassRoomPlanFragmentFragment) => void;
  deleting?: boolean;
  onDetachLastMerge?: (plan: ClassRoomPlanFragmentFragment) => void;
  detachingLastMerge?: boolean;
  onMergeWithNextPlan?: (plan: ClassRoomPlanFragmentFragment) => void;
  mergingWithNextPlan?: boolean;
};

export function ClassRoomPlanListItem({
  plan,
  onDelete,
  deleting,
  onEdit,
  onDetachLastMerge,
  detachingLastMerge,
  onMergeWithNextPlan,
  mergingWithNextPlan,
}: Props) {
  const startDate = dayjs(plan.startedAtWeekUtc);
  const endDate = dayjs(plan.endedAtWeekUtc);

  function printWeek(weekDate: Dayjs): string {
    return `${weekDate.week()} of ${weekDate.year()}`;
  }

  function formatWeekDate(weekDate: Dayjs): string {
    return formatDate(weekDate.utc(), { withWeekday: true });
  }

  return (
    <Card>
      <div className={"flex space-x-2 justify-between"}>
        <div className={"space-y-2 flex-1"}>
          <p>
            From week{" "}
            <NormaPopover
              activator={
                <b className={"underline decoration-dotted underline-offset-4"}>
                  {printWeek(startDate)}
                </b>
              }
            >
              <p className={"whitespace-nowrap"}>{formatWeekDate(startDate)}</p>
            </NormaPopover>{" "}
            to week{" "}
            <NormaPopover
              activator={
                <b className={"underline decoration-dotted underline-offset-4"}>
                  {printWeek(endDate)}
                </b>
              }
            >
              <p className={"whitespace-nowrap"}>{formatWeekDate(endDate)}</p>
            </NormaPopover>{" "}
            with{" "}
            {plan.unlimitedLessonsPerWeekEnabled ? (
              "unlimited lessons per week"
            ) : (
              <>
                <b>{plan.lessonsPerWeek}</b> lessons per week
              </>
            )}
          </p>
          <p>{plan.numberOfSlotIncluded} included hours in this plan</p>
          {plan.bonuses && plan.bonuses.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {plan.bonuses.map((bonus, i) => (
                <BonusBadge key={i} bonus={bonus} />
              ))}
            </div>
          )}
          {plan.mergeHistory && plan.mergeHistory.length > 0 && (
            <div className="mt-3 border-t pt-3 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Merge history
              </p>
              {plan.mergeHistory.map((entry, i) => (
                <div
                  key={i}
                  className="text-xs text-slate-600 bg-slate-50 rounded p-2 space-y-0.5"
                >
                  <p>
                    <span className="font-medium">Merged on:</span>{" "}
                    {formatDateTime(entry.mergedAtUtc)}
                  </p>
                  <p>
                    <span className="font-medium">End date before merge:</span>{" "}
                    {formatDate(dayjs(entry.premergeEndedAtWeekUtc).utc(), {
                      withWeekday: true,
                    })}
                  </p>
                  <p>
                    <span className="font-medium">Lessons before merge:</span>{" "}
                    {entry.premergeTotalLessons}
                  </p>
                  {entry.triggeringOrderId && (
                    <p>
                      <span className="font-medium">Order:</span>{" "}
                      {entry.triggeringOrderId}
                    </p>
                  )}
                  {entry.promoIds && entry.promoIds.length > 0 && (
                    <p>
                      <span className="font-medium">Promos:</span>{" "}
                      {entry.promoIds.join(", ")}
                    </p>
                  )}
                  {entry.bonuses && entry.bonuses.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      <span className="font-medium">Bonuses added:</span>
                      {entry.bonuses.map((bonus, j) => (
                        <BonusBadge key={j} bonus={bonus} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {(onDetachLastMerge || onMergeWithNextPlan) && (
            <div className="flex flex-wrap gap-4 pt-1 text-sm">
              {onDetachLastMerge && (
                <TextButton
                  label={"Detach last merged plan"}
                  loading={detachingLastMerge}
                  onClick={() => onDetachLastMerge(plan)}
                />
              )}
              {onMergeWithNextPlan && (
                <TextButton
                  label={"Merge with next plan"}
                  loading={mergingWithNextPlan}
                  onClick={() => onMergeWithNextPlan(plan)}
                />
              )}
            </div>
          )}
        </div>
        <div className={"flex space-x-2 items-start"}>
          <PencilSquareIcon
            onClick={() => onEdit(plan)}
            className={
              "w-5 h-5 text-slate-600 hover:text-slate-800 cursor-pointer"
            }
          />
          {deleting ? (
            <PrimaryLoadingIndicator size={LoaderSize.Small} />
          ) : (
            <TrashIcon
              onClick={() => onDelete(plan.id)}
              className={"w-5 text-red-600 cursor-pointer"}
            />
          )}
        </div>
      </div>
    </Card>
  );
}
